import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText, Download, Send, ChevronDown, CheckCircle2, Maximize2, X } from "lucide-react";
import AmenitiesStrip from "../components/site/AmenitiesStrip";
import PhotoMarquee from "../components/site/PhotoMarquee";
import { api, assetUrl } from "../lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "../lib/constants";
import EnquiryDialog from "../components/site/EnquiryDialog";
import PlanImageViewer from "../components/site/PlanImageViewer";
import HotspotPlan from "../components/site/HotspotPlan";
import LotDetailDialog from "../components/site/LotDetailDialog";
import { usePageMetaEffect } from "../seo/pageMeta";
import LotCompare from "../components/site/LotCompare";

const chipTone = (status) => {
  if (status === "available") return "bg-[#C9B79C] text-[#12201A]";
  if (status === "completed") return "bg-[#26402F] text-[#F3EEE3]";
  return "bg-[#1B2E24] text-[#F3EEE3]/70";
};

// Stages that get a full-screen video hero (number -> assets in /public/hero).
const HERO_VIDEOS = {
  7: { video: "/hero/stage7_hero.mp4", poster: "/hero/stage7_hero_poster.jpg" },
  8: { video: "/hero/stage8_hero.mp4", poster: "/hero/stage8_hero_poster.jpg" },
};

const Seg = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-full border border-[#F3EEE3]/15 bg-[#0C1510] p-1" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o.v}
        onClick={() => onChange(o.v)}
        data-testid={`${testid}-${o.v}`}
        className={`px-3 py-1.5 rounded-full text-xs transition-colors ${value === o.v ? "bg-[#C9B79C] text-[#12201A] font-medium" : "text-[#F3EEE3]/70 hover:text-[#F3EEE3]"}`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

const StageDetail = () => {
  const { id } = useParams();
  const [stage, setStage] = useState(null);
  const [parcels, setParcels] = useState([]);
  const [docs, setDocs] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [enq, setEnq] = useState(null);
  const [detail, setDetail] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [fAvail, setFAvail] = useState("all");
  const [fWalk, setFWalk] = useState("all");
  const [fArea, setFArea] = useState("all");
  const [sortKey, setSortKey] = useState("lot");
  const [compare, setCompare] = useState([]);
  const toggleCompare = (p) => setCompare((c) => c.find((x) => x.id === p.id) ? c.filter((x) => x.id !== p.id) : (c.length >= 4 ? c : [...c, p]));

  useEffect(() => {
    api.get(`/stages/${id}`).then((r) => setStage(r.data)).catch(() => {});
    api.get(`/parcels?stage_id=${id}`).then((r) => setParcels(r.data)).catch(() => {});
    api.get(`/documents?stage_id=${id}`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/builders`).then((r) => setBuilders(r.data)).catch(() => {});
  }, [id]);

  usePageMetaEffect(
    stage ? `${stage.name} · ${stage.status_label || "Stage"} · Deer Park Estates` : null,
    stage ? `${stage.description || stage.name}${parcels.length ? ` ${parcels.length} lots.` : ""} Deer Park Estates, Spruce Grove, Alberta.` : null,
  );

  const logoFor = (name) => builders.find((b) => b.name === name)?.logo_url || null;
  const nm = (s) => (s || "").toLowerCase().trim();
  const builderFor = (p) => {
    if (!p) return null;
    return (
      builders.find((b) => b.id === p.builder_id) ||
      builders.find((b) => p.builder_name && (nm(b.name).includes(nm(p.builder_name)) || nm(p.builder_name).includes(nm(b.name)))) ||
      null
    );
  };
  const stageBuilders = Array.from(
    new Map(
      parcels.map((p) => builderFor(p)).filter(Boolean).map((b) => [b.id, b])
    ).values()
  );

  if (!stage) return <div className="pt-[120px] text-center text-[#F3EEE3]/50">Loading…</div>;
  const selling = stage.status === "available";
  const heroCfg = HERO_VIDEOS[stage.number] || null;
  const isStage8 = !!heroCfg;
  const sorted = [...parcels].sort((a, b) => {
    const bl = String(a.block || "").localeCompare(String(b.block || ""));
    return bl !== 0 ? bl : (a.lot_number || 0) - (b.lot_number || 0);
  });
  const hasBlocks = sorted.some((p) => p.block);
  const hasArea = sorted.some((p) => p.area_sqft);
  const hasWalk = sorted.some((p) => p.walkout);
  const hasFront = sorted.some((p) => p.frontage_m);
  const showFilters = hasArea || hasWalk || hasFront;
  const matches = (p) => {
    if (fAvail !== "all" && p.status !== fAvail) return false;
    if (fWalk === "walkout" && !p.walkout) return false;
    if (fWalk === "none" && p.walkout) return false;
    if (fArea !== "all") {
      const a = p.area_sqft || 0;
      if (fArea === "s" && a >= 6400) return false;
      if (fArea === "m" && (a < 6400 || a > 6900)) return false;
      if (fArea === "l" && a <= 6900) return false;
    }
    return true;
  };
  const sortFns = {
    lot: (a, b) => String(a.block || "").localeCompare(String(b.block || "")) || (a.lot_number || 0) - (b.lot_number || 0),
    area_desc: (a, b) => (b.area_sqft || 0) - (a.area_sqft || 0) || (b.frontage_m || 0) - (a.frontage_m || 0) || (a.lot_number || 0) - (b.lot_number || 0),
    area_asc: (a, b) => (a.area_sqft || 0) - (b.area_sqft || 0) || (a.frontage_m || 0) - (b.frontage_m || 0) || (a.lot_number || 0) - (b.lot_number || 0),
    front_desc: (a, b) => (b.frontage_m || 0) - (a.frontage_m || 0) || (b.area_sqft || 0) - (a.area_sqft || 0) || (a.lot_number || 0) - (b.lot_number || 0),
    front_asc: (a, b) => (a.frontage_m || 0) - (b.frontage_m || 0) || (a.area_sqft || 0) - (b.area_sqft || 0) || (a.lot_number || 0) - (b.lot_number || 0),
  };
  const filtered = (showFilters ? sorted.filter(matches) : sorted).slice().sort(sortFns[sortKey]);
  const statusesPresent = [...new Set(sorted.map((p) => p.status))];
  const blocks = hasBlocks
    ? [...new Set(filtered.map((p) => p.block))].map((label) => ({ label, items: filtered.filter((p) => p.block === label) }))
    : [{ label: null, items: filtered }];
  const pins = filtered.filter((p) => p.hotspot && typeof p.hotspot.x === "number").map((p) => ({
    id: p.id, x: p.hotspot.x, y: p.hotspot.y, label: String(p.lot_number ?? p.parcel_no),
    color: STATUS_COLORS[p.status], title: `Lot ${p.lot_number ?? p.parcel_no}${p.builder_name ? " · " + p.builder_name : ""}`, parcel: p,
  }));
  // group lots by builder (unassigned → Available lots) for the accordion
  const lotGroups = (() => {
    const map = new Map();
    filtered.forEach((p) => {
      const key = p.builder_name || "__available__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    const entries = [...map.entries()].map(([key, items]) => ({
      key, label: key === "__available__" ? "Available lots" : key, items,
    }));
    entries.sort((a, b) => {
      if (a.key === "__available__") return 1;
      if (b.key === "__available__") return -1;
      return a.label.localeCompare(b.label);
    });
    return entries;
  })();

  return (
    <div className="bg-[#12201A] min-h-screen">
      {isStage8 && (
        <section className="relative h-[100svh] min-h-[600px] w-full overflow-hidden grain" data-testid="stage-video-hero">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={heroCfg.poster}
            data-testid="stage-hero-video"
          >
            <source src={heroCfg.video} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C1510]/45 via-[#0C1510]/10 to-[#0C1510]/80" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C1510]/50 via-transparent to-transparent" />
          <div className="relative z-10 h-full max-w-[1200px] mx-auto px-5 md:px-8 flex flex-col justify-center">
            <Link to="/stages" className="inline-flex items-center gap-2 text-[#C9B79C] text-sm mb-8 w-fit"><ArrowLeft size={15} /> All stages</Link>
            <div className="flex flex-wrap items-center gap-4">
              <p className="eyebrow text-[#C9B79C]">Stage {String(stage.number).padStart(2, "0")}</p>
              <span className={`text-[0.62rem] tracking-wide uppercase px-3 py-1 rounded-full ${chipTone(stage.status)}`}>{stage.status_label}</span>
            </div>
            <h1 className="font-display font-light text-5xl md:text-7xl text-[#F3EEE3] mt-3 leading-[1.02]">{stage.name}</h1>
            <p className="text-[#F3EEE3]/75 mt-5 max-w-2xl leading-relaxed text-base md:text-lg font-light">{stage.description}</p>
            <Link to="/builders" data-testid="stage-homebuyer-cta" className="group mt-7 inline-flex items-center gap-3 w-fit text-[#F3EEE3] text-2xl md:text-4xl font-display">
              <span className="font-semibold">Looking for a home? <span className="text-[#C9B79C] link-underline font-normal">Meet the builders</span></span>
              <ArrowRight size={30} className="text-[#C9B79C] group-hover:translate-x-1.5 transition-transform shrink-0" />
            </Link>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-[#F3EEE3]/55">
            <span className="eyebrow text-[0.5rem]">Scroll to explore</span>
            <ChevronDown size={16} className="animate-bounce" />
          </div>
        </section>
      )}
      <div className={`${isStage8 ? "pt-4" : "pt-[64px]"} bg-[#12201A]`}>
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14">
          {!isStage8 && (
            <>
              <Link to="/stages" className="inline-flex items-center gap-2 text-[#C9B79C] text-sm mb-8"><ArrowLeft size={15} /> All stages</Link>
              <div className="flex flex-wrap items-center gap-4">
                <p className="eyebrow text-[#C9B79C]">Stage {String(stage.number).padStart(2, "0")}</p>
                <span className={`text-[0.62rem] tracking-wide uppercase px-3 py-1 rounded-full ${chipTone(stage.status)}`}>{stage.status_label}</span>
              </div>
              <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3] mt-2">{stage.name}</h1>
              <p className="text-[#F3EEE3]/60 mt-3 max-w-2xl leading-relaxed">{stage.description}</p>
              <Link to="/builders" data-testid="stage-homebuyer-cta" className="group mt-5 inline-flex items-center gap-3 w-fit text-[#F3EEE3] text-xl md:text-2xl font-display font-light">
                <span>Looking for a home? <span className="text-[#C9B79C] link-underline">Meet the builders</span></span>
                <ArrowRight size={22} className="text-[#C9B79C] group-hover:translate-x-1.5 transition-transform shrink-0" />
              </Link>
            </>
          )}

        {/* summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
          <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
            <p className="font-display text-3xl text-[#F3EEE3]">{parcels.length || stage.parcels_count || "—"}</p>
            <p className="eyebrow text-[0.5rem] text-[#C9B79C] mt-1">Lots</p>
          </div>
          <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
            <p className="font-display text-3xl text-[#F3EEE3]">{stage.status_label}</p>
            <p className="eyebrow text-[0.5rem] text-[#C9B79C] mt-1">Status</p>
          </div>
          <div className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
            <p className="font-display text-3xl text-[#F3EEE3]">{docs.length}</p>
            <p className="eyebrow text-[0.5rem] text-[#C9B79C] mt-1">Documents</p>
          </div>
        </div>

        {/* community amenities */}
        <AmenitiesStrip />

        {/* builders in this stage */}
        {stageBuilders.length > 0 && (
          <div className="mb-14" data-testid="stage-builders">
            <p className="eyebrow text-[#C9B79C] mb-2">Our Builders</p>
            <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mb-6">Builders in this stage</h2>
            <div className="flex gap-4 md:gap-6 overflow-x-auto hide-scrollbar pb-2">
              {stageBuilders.map((b, i) => {
                const cover = b.gallery?.[0] || b.logo_url;
                return (
                  <Link
                    key={b.id}
                    to={`/builders/${b.id}`}
                    data-testid={`stage-builder-${i}`}
                    className="group shrink-0 w-[78%] sm:w-[46%] md:w-[320px] rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] hover:border-[#C9B79C]/40 transition-colors"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-[#0C1510]">
                      {cover ? (
                        <img src={assetUrl(cover)} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-display text-2xl text-[#F3EEE3]/40">{b.name}</div>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="font-display text-lg text-[#F3EEE3]">{b.name}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-3 group-hover:gap-2.5 transition-all">See details <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* photo gallery */}
        {stage.photo_gallery?.length > 0 && (
          <div className="mt-12" data-testid="stage-photo-gallery">
            <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3]">Gallery</h2>
            <p className="text-[#F3EEE3]/50 text-sm mt-1 mb-5">Photos from around the community. Tap any image to enlarge.</p>
            <div className="relative left-1/2 right-1/2 -mx-[50vw] w-screen">
              <PhotoMarquee photos={stage.photo_gallery} onOpen={(g) => setLightbox(g)} />
            </div>
          </div>
        )}

        {/* interactive plan */}
        {stage.plan_image && (
          <div className="mt-10">
            <h2 className="font-display text-2xl text-[#F3EEE3] mb-4">Interactive stage plan</h2>
            {pins.length > 0 ? (
              <>
                <HotspotPlan src={stage.plan_image} pins={pins} onPinClick={(pin) => setDetail(pin.parcel)} fit />
                <p className="text-[#F3EEE3]/55 text-sm mt-3">Tap a numbered lot on the plan to see its size, features and availability.</p>
              </>
            ) : (
              <PlanImageViewer src={stage.plan_image} alt={`${stage.name} plan`} />
            )}
            {sorted.length > 0 && (
              <div className="mt-5 rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <p className="eyebrow text-[0.55rem] text-[#C9B79C]">Pick a lot — tap for details</p>
                  <div className="flex items-center gap-4 text-xs text-[#F3EEE3]/60" data-testid="lot-legend">
                    {statusesPresent.map((st) => (
                      <span key={st} className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[st] }} /> {STATUS_LABELS[st]}
                      </span>
                    ))}
                  </div>
                </div>
                {showFilters && (
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-5 pb-4 border-b border-[#F3EEE3]/10" data-testid="lot-filters">
                    {statusesPresent.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className="text-[#F3EEE3]/45 text-xs">Availability</span>
                        <Seg testid="filter-avail" value={fAvail} onChange={setFAvail} options={[{ v: "all", l: "All" }, { v: "available", l: "Available" }, { v: statusesPresent.includes("reserved") ? "reserved" : "sold", l: statusesPresent.includes("reserved") ? "Reserved" : STATUS_LABELS.sold }]} />
                      </div>
                    )}
                    {hasWalk && (
                      <div className="flex items-center gap-2">
                        <span className="text-[#F3EEE3]/45 text-xs">Walkout</span>
                        <Seg testid="filter-walk" value={fWalk} onChange={setFWalk} options={[{ v: "all", l: "All" }, { v: "walkout", l: "Walkout" }, { v: "none", l: "No walkout" }]} />
                      </div>
                    )}
                    {hasArea && (
                      <div className="flex items-center gap-2">
                        <span className="text-[#F3EEE3]/45 text-xs">Lot area (sq ft)</span>
                        <Seg testid="filter-area" value={fArea} onChange={setFArea} options={[{ v: "all", l: "Any" }, { v: "s", l: "< 6,400" }, { v: "m", l: "6,400–6,900" }, { v: "l", l: "> 6,900" }]} />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-[#F3EEE3]/45 text-xs">Sort</span>
                      <select
                        data-testid="lot-sort"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        className="bg-[#0C1510] border border-[#F3EEE3]/15 rounded-full text-xs text-[#F3EEE3] px-3 py-1.5 focus:outline-none focus:border-[#C9B79C] cursor-pointer"
                      >
                        <option value="lot">Lot number</option>
                        {hasArea && <option value="area_desc">Area: large → small</option>}
                        {hasArea && <option value="area_asc">Area: small → large</option>}
                        {hasFront && <option value="front_desc">Frontage: wide → narrow</option>}
                        {hasFront && <option value="front_asc">Frontage: narrow → wide</option>}
                      </select>
                    </div>
                    {(fAvail !== "all" || fWalk !== "all" || fArea !== "all") && (
                      <button onClick={() => { setFAvail("all"); setFWalk("all"); setFArea("all"); }} data-testid="filter-reset" className="text-xs text-[#C9B79C] hover:text-[#F3EEE3] underline underline-offset-2">Reset</button>
                    )}
                    <span className="text-[#F3EEE3]/50 text-xs ml-auto" data-testid="filter-count">{filtered.length} of {sorted.length} lots</span>
                  </div>
                )}
                {filtered.length === 0 ? (
                  <p className="text-[#F3EEE3]/50 text-sm py-4" data-testid="filter-empty">No lots match these filters. <button onClick={() => { setFAvail("all"); setFWalk("all"); setFArea("all"); }} className="text-[#C9B79C] underline">Clear filters</button></p>
                ) : blocks.map(({ label, items }) => (
                  <div key={label || "all"} className="mb-4 last:mb-0">
                    {label && <p className="text-[#F3EEE3]/45 text-xs mb-2">Block {label}</p>}
                    <div className="flex flex-wrap gap-2">
                      {items.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setDetail(p)}
                          data-testid={`pick-lot-${p.parcel_no}`}
                          title={p.builder_name || STATUS_LABELS[p.status]}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border text-[#F3EEE3] hover:scale-105 transition-transform"
                          style={{ borderColor: STATUS_COLORS[p.status] + "66", background: STATUS_COLORS[p.status] + "1A" }}
                        >
                          <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[p.status] }} /> Lot {p.lot_number ?? p.parcel_no}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* lots — grouped by builder (accordion) */}
        {sorted.length > 0 && (
          <>
            <h2 className="font-display text-2xl text-[#F3EEE3] mt-12 mb-5">Lots in this stage ({filtered.length}{showFilters && filtered.length !== sorted.length ? ` of ${sorted.length}` : ""})</h2>
            <div className="space-y-3" data-testid="stage-lots-accordion">
              {lotGroups.map(({ key, label, items }) => {
                const open = !!openGroups[key];
                return (
                  <div key={key} className="rounded-2xl border border-[#F3EEE3]/12 overflow-hidden bg-[#0F1B15]">
                    <button
                      type="button"
                      onClick={() => setOpenGroups((g) => ({ ...g, [key]: !g[key] }))}
                      data-testid={`lot-group-${key}`}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#F3EEE3]/[0.03] transition-colors"
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: key === "__available__" ? STATUS_COLORS.available : "#C9B79C" }} />
                        <span className="text-[#F3EEE3] font-medium truncate">{label}</span>
                        <span className="text-[#C9B79C] text-sm shrink-0">({items.length})</span>
                      </span>
                      <ChevronDown size={18} className={`text-[#F3EEE3]/55 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="overflow-x-auto border-t border-[#F3EEE3]/10">
                        <table className="w-full text-sm" data-testid={`stage-lots-table-${key}`}>
                          <thead>
                            <tr className="text-left text-[#F3EEE3]/50 eyebrow text-[0.55rem] border-b border-[#F3EEE3]/12">
                              <th className="px-5 py-3">Lot</th>
                              {hasBlocks && <th className="px-5 py-3">Block</th>}
                              <th className="px-5 py-3">Area</th>
                              <th className="px-5 py-3">Frontage</th>
                              <th className="px-5 py-3">Status</th>
                              <th className="px-5 py-3" />
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((p) => (
                              <tr key={p.id} className="border-b border-[#F3EEE3]/8 last:border-0 hover:bg-[#F3EEE3]/[0.03] cursor-pointer" onClick={() => setDetail(p)} data-testid={`stage-lot-row-${p.parcel_no}`}>
                                <td className="px-5 py-4 text-[#F3EEE3] font-medium">{p.lot_number ?? p.parcel_no}</td>
                                {hasBlocks && <td className="px-5 py-4 text-[#F3EEE3]/70">{p.block || "—"}</td>}
                                <td className="px-5 py-4 text-[#F3EEE3]/70">{p.area_sqft ? `${Number(p.area_sqft).toLocaleString()} sq ft` : "—"}</td>
                                <td className="px-5 py-4 text-[#F3EEE3]/70">{p.frontage_m ? `${p.frontage_m} m` : "—"}</td>
                                <td className="px-5 py-4">
                                  <span className="inline-flex items-center gap-2 text-[#F3EEE3]/80">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[p.status] }} /> {STATUS_LABELS[p.status]}
                                  </span>
                                </td>
                                <td className="px-5 py-4 text-right">
                                  <button onClick={(e) => { e.stopPropagation(); setDetail(p); }} data-testid={`stage-details-${p.parcel_no}`} className="inline-flex items-center gap-1.5 text-[#C9B79C] hover:text-[#F3EEE3] text-xs">
                                    Details <ArrowLeft size={13} className="rotate-180" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* for builders — servicing & engineering */}
        {(stage.highlights?.length > 0 || stage.spec_gallery?.length > 0) && (
          <div className="mt-16" data-testid="stage-builder-info">
            <p className="eyebrow text-[#C9B79C] mb-2">For builders</p>
            <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3]">Servicing &amp; engineering</h2>
            {stage.spec_intro && <p className="text-[#F3EEE3]/65 mt-3 max-w-3xl leading-relaxed">{stage.spec_intro}</p>}

            {stage.servicing?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="stage-servicing">
                {stage.servicing.map((s, i) => (
                  <div key={i} className="rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-4 py-3">
                    <p className="eyebrow text-[0.5rem] text-[#C9B79C]">{s.label}</p>
                    <p className="text-[#F3EEE3] text-sm mt-1 font-medium">{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            {stage.highlights?.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4 mt-8">
                {stage.highlights.map((h, i) => (
                  <div key={i} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 size={18} className="text-[#C9B79C] shrink-0" />
                      <p className="text-[#F3EEE3] font-medium">{h.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {stage.spec_gallery?.length > 0 && (
              <>
                <h3 className="font-display text-xl text-[#F3EEE3] mt-12 mb-1">Engineering drawings</h3>
                <p className="text-[#F3EEE3]/50 text-sm mb-5">Tap any drawing to view it full-size. Full plan set is downloadable below.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stage.spec_gallery.map((g, i) => (
                    <button key={i} onClick={() => setLightbox(g)} data-testid={`stage-drawing-${i}`} className="group text-left rounded-2xl overflow-hidden border border-[#F3EEE3]/12 hover:border-[#C9B79C]/50 transition-colors">
                      <div className="aspect-[3/2] overflow-hidden bg-white">
                        <img src={assetUrl(g.image)} alt={g.title} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
                      </div>
                      <div className="p-4 bg-[#0F1B15]">
                        <p className="text-[#F3EEE3] text-sm font-medium flex items-center gap-2"><Maximize2 size={13} className="text-[#C9B79C] shrink-0" /> {g.title}</p>
                        <p className="text-[#F3EEE3]/55 text-xs mt-1.5 leading-relaxed line-clamp-2">{g.caption}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* docs */}
        {docs.length > 0 && (
          <>
            <h2 className="font-display text-2xl text-[#F3EEE3] mt-12 mb-5">Plans &amp; documents</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {docs.map((d) => (
                <a key={d.id} href={assetUrl(`/api/documents/${d.id}/download`)} target="_blank" rel="noreferrer" data-testid={`stage-doc-${d.id}`} className="flex items-center justify-between rounded-xl border border-[#F3EEE3]/12 bg-[#0F1B15] px-5 py-4 hover:border-[#C9B79C]/40">
                  <span className="flex items-center gap-3 text-[#F3EEE3] text-sm"><FileText size={16} className="text-[#C9B79C]" /> {d.title}</span>
                  <Download size={15} className="text-[#F3EEE3]/50" />
                </a>
              ))}
            </div>
          </>
        )}

        {selling && (
          <div className="mt-12 rounded-2xl border border-[#C9B79C]/25 bg-[#0F1B15] p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <p className="text-[#F3EEE3] text-lg">Interested in a lot in {stage.name}?</p>
            <button onClick={() => setEnq({})} className="inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-7 py-3.5 rounded-full font-medium hover:bg-white transition-colors">
              <Send size={16} /> Request Information
            </button>
          </div>
        )}
        </div>
      </div>

      <LotDetailDialog
        open={!!detail}
        parcel={detail}
        builder={builderFor(detail)}
        stageNumber={stage.number}
        inCompare={!!compare.find((x) => x.id === detail?.id)}
        onToggleCompare={toggleCompare}
        onClose={() => setDetail(null)}
        onEnquire={(p) => { setDetail(null); setEnq(p); }}
      />

      <LotCompare items={compare} onRemove={toggleCompare} onClear={() => setCompare([])} />

      {lightbox && createPortal(
        <div className="fixed inset-0 z-[200] bg-black/92 flex flex-col" data-testid="drawing-lightbox" onClick={() => setLightbox(null)}>
          <div className="flex items-start justify-between gap-4 px-5 py-4 text-[#F3EEE3] border-b border-white/10 bg-[#0C1510]">
            <div className="min-w-0">
              <p className="font-medium truncate">{lightbox.title}</p>
              <p className="text-[#F3EEE3]/60 text-xs mt-0.5 max-w-2xl hidden sm:block">{lightbox.caption}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <a href={assetUrl(lightbox.image)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid="drawing-open-full" className="text-[#C9B79C] text-sm inline-flex items-center gap-1.5 hover:text-[#F3EEE3]"><Maximize2 size={14} /> Open full</a>
              <button onClick={() => setLightbox(null)} data-testid="drawing-close" className="text-[#F3EEE3]/70 hover:text-white"><X size={22} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-3 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <img src={assetUrl(lightbox.image)} alt={lightbox.title} className="mx-auto max-w-full bg-white rounded-lg shadow-2xl" />
          </div>
        </div>,
        document.body
      )}

      <EnquiryDialog
        open={!!enq}
        onClose={() => setEnq(null)}
        context={{ parcel_id: enq?.id, stage_id: id, label: enq?.lot_number ? `${stage.name} · Lot ${enq.lot_number}` : stage.name, source: `stage:${stage.number}`, builderName: enq?.builder_name, builderLogo: logoFor(enq?.builder_name) }}
      />
    </div>
  );
};

export default StageDetail;
